import { useEffect, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import {
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Pencil,
  RefreshCw,
  Check,
  X,
} from 'lucide-react';
import { nanoid } from 'nanoid';
import { useVault } from '../../../core/lib/vault';
import {
  acquireAttachmentUrl,
  releaseAttachmentUrl,
} from '../lib/attachmentBlobCache';
import { openAttachmentPreview } from '../lib/attachmentPreview';

export type InlineImageAlign = 'left' | 'center' | 'right' | null;

/** Custom event dispatched by the inline-image NodeView when the user picks a
 * replacement file. EditorPane listens, runs uploadFile against the doc's
 * customerId + vaultKey, then commits the swap via setInlineImageAttachmentId.
 * Mirrors the existing dragonvault:password-field event pattern — keeps the
 * NodeView agnostic about which document/customer it lives in. */
export const INLINE_IMAGE_REPLACE_EVENT = 'dragonvault:inline-image-replace';
export interface InlineImageReplaceDetail {
  id: string;
  file: File;
}

export function mintInlineImageId(): string {
  return nanoid(10);
}

export const InlineImage = Node.create({
  name: 'inlineImage',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('id') ?? '',
        renderHTML: (attrs: Record<string, any>) => (attrs.id ? { id: attrs.id } : {}),
      },
      attachmentId: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('attachmentId') ?? '',
        renderHTML: (attrs: Record<string, any>) => ({ attachmentId: attrs.attachmentId }),
      },
      width: {
        default: null,
        parseHTML: (el: HTMLElement) => {
          const raw = el.getAttribute('width');
          if (raw == null || raw === '') return null;
          const n = Number(raw);
          return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
        },
        renderHTML: (attrs: Record<string, any>) =>
          attrs.width != null ? { width: String(attrs.width) } : {},
      },
      alt: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('alt') ?? '',
        renderHTML: (attrs: Record<string, any>) => (attrs.alt ? { alt: attrs.alt } : {}),
      },
      align: {
        default: null,
        parseHTML: (el: HTMLElement) => {
          const raw = el.getAttribute('align');
          return raw === 'left' || raw === 'center' || raw === 'right' ? raw : null;
        },
        renderHTML: (attrs: Record<string, any>) => (attrs.align ? { align: attrs.align } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'inline-image' }];
  },

  renderHTML({ HTMLAttributes }) {
    // Atom nodes have no children, but Turndown drops "blank" elements
    // (empty textContent) before our markdown Turndown rule can fire. A
    // zero-width space keeps the tag visible to Turndown without affecting
    // user rendering (the React NodeView replaces this DOM entirely).
    return ['inline-image', mergeAttributes(HTMLAttributes), '\u200b'];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InlineImageNodeView);
  },

  addCommands() {
    return {
      insertInlineImage:
        (opts: { attachmentId: string; alt?: string; width?: number | null; id?: string }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) => {
          const id = opts.id && opts.id.length > 0 ? opts.id : mintInlineImageId();
          const node = state.schema.nodes.inlineImage.create({
            id,
            attachmentId: opts.attachmentId,
            alt: opts.alt ?? '',
            width: opts.width ?? null,
          });
          if (dispatch) dispatch(state.tr.replaceSelectionWith(node));
          return true;
        },
      setInlineImageWidth:
        (opts: { id: string; width: number | null }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) =>
          updateInlineImageAttr(state, dispatch, opts.id, 'width', opts.width),
      setInlineImageAlign:
        (opts: { id: string; align: InlineImageAlign }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) =>
          updateInlineImageAttr(state, dispatch, opts.id, 'align', opts.align),
      setInlineImageAlt:
        (opts: { id: string; alt: string }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) =>
          updateInlineImageAttr(state, dispatch, opts.id, 'alt', opts.alt),
      setInlineImageAttachmentId:
        (opts: { id: string; attachmentId: string }) =>
        ({ state, dispatch }: { state: any; dispatch: any }) => {
          // Replace-image flow: clear width so the new image sizes naturally.
          let foundPos = -1;
          state.doc.descendants((n: any, pos: number) => {
            if (foundPos >= 0) return false;
            if (n.type.name === 'inlineImage' && n.attrs.id === opts.id) {
              foundPos = pos;
              return false;
            }
            return true;
          });
          if (foundPos < 0) return false;
          if (dispatch) {
            const tr = state.tr;
            const node = state.doc.nodeAt(foundPos);
            tr.setNodeMarkup(foundPos, undefined, {
              ...(node ? node.attrs : {}),
              attachmentId: opts.attachmentId,
              width: null,
            });
            dispatch(tr);
          }
          return true;
        },
    } as any;
  },
});

function updateInlineImageAttr(
  state: any,
  dispatch: any,
  id: string,
  attr: string,
  value: unknown,
): boolean {
  let foundPos = -1;
  state.doc.descendants((n: any, pos: number) => {
    if (foundPos >= 0) return false;
    if (n.type.name === 'inlineImage' && n.attrs.id === id) {
      foundPos = pos;
      return false;
    }
    return true;
  });
  if (foundPos < 0) return false;
  if (dispatch) {
    const tr = state.tr;
    const setAttr = (tr as any).setNodeAttribute?.bind(tr);
    if (setAttr) {
      setAttr(foundPos, attr, value);
    } else {
      const node = state.doc.nodeAt(foundPos);
      tr.setNodeMarkup(foundPos, undefined, {
        ...(node ? node.attrs : {}),
        [attr]: value,
      });
    }
    dispatch(tr);
  }
  return true;
}

function InlineImageNodeView(props: NodeViewProps) {
  const { node, editor } = props;
  const vaultKey = useVault((s) => s.vaultKey);
  const [url, setUrl] = useState<string | null>(null);
  const [mimetype, setMimetype] = useState('');
  const [filename, setFilename] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [editingAlt, setEditingAlt] = useState(false);
  const [altDraft, setAltDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const id = (node.attrs.id as string) || '';
  const attachmentId = (node.attrs.attachmentId as string) || '';
  const width = node.attrs.width as number | null;
  const align = (node.attrs.align as InlineImageAlign) ?? null;
  const altAttr = (node.attrs.alt as string) || '';
  const alt = altAttr || filename || 'image';
  const editable = editor.isEditable;
  void mimetype;

  function setAlign(next: InlineImageAlign) {
    (editor.commands as any).setInlineImageAlign({ id, align: next });
  }

  function startEditAlt() {
    setAltDraft(altAttr);
    setEditingAlt(true);
  }

  function commitAlt() {
    (editor.commands as any).setInlineImageAlt({ id, alt: altDraft });
    setEditingAlt(false);
  }

  function cancelAlt() {
    setEditingAlt(false);
  }

  function pickReplacement() {
    fileInputRef.current?.click();
  }

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    // Hand the file to EditorPane — only it knows the doc's customerId.
    window.dispatchEvent(
      new CustomEvent<InlineImageReplaceDetail>(INLINE_IMAGE_REPLACE_EVENT, {
        detail: { id, file: f },
      }),
    );
    e.target.value = '';
  }

  useEffect(() => {
    let cancelled = false;
    if (!vaultKey || !attachmentId) return;
    (async () => {
      try {
        const r = await acquireAttachmentUrl(attachmentId, vaultKey);
        if (cancelled) {
          releaseAttachmentUrl(attachmentId);
          return;
        }
        setUrl(r.url);
        setMimetype(r.mimetype);
        setFilename(r.filename);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? 'Could not load image');
      }
    })();
    return () => {
      cancelled = true;
      if (attachmentId) releaseAttachmentUrl(attachmentId);
    };
  }, [attachmentId, vaultKey]);

  function onImageClick(e: React.MouseEvent) {
    if (editable) return;
    e.preventDefault();
    e.stopPropagation();
    if (attachmentId) openAttachmentPreview(attachmentId);
  }

  function startResize(e: React.PointerEvent) {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const wrapper = e.currentTarget.parentElement;
    const img = wrapper?.querySelector('img') as HTMLImageElement | null;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const startX = e.clientX;
    const startW = rect.width;
    const aspect =
      img.naturalHeight > 0 && img.naturalWidth > 0
        ? img.naturalWidth / img.naturalHeight
        : rect.height > 0
          ? rect.width / rect.height
          : 1;
    const MIN = 64;
    const MAX_PX = 1600;
    let latest = Math.round(startW);

    function onMove(ev: PointerEvent) {
      latest = Math.max(
        MIN,
        Math.min(MAX_PX, Math.round(startW + (ev.clientX - startX))),
      );
      if (img) {
        img.style.width = `${latest}px`;
        img.style.height = `${Math.round(latest / aspect)}px`;
      }
    }
    function onUp() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (img) img.style.height = 'auto';
      (editor.commands as any).setInlineImageWidth({ id, width: latest });
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  return (
    <NodeViewWrapper
      as="div"
      className="inline-image group my-2"
      data-drag-handle
      data-align={align ?? undefined}
    >
      <div
        className="relative inline-block max-w-full align-top"
        contentEditable={false}
      >
        {url ? (
          <img
            src={url}
            alt={alt}
            onClick={onImageClick}
            draggable={false}
            style={{
              width: width ? `${width}px` : undefined,
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              cursor: editable ? 'default' : 'zoom-in',
              borderRadius: 4,
            }}
          />
        ) : err ? (
          <div className="px-3 py-6 bg-ink-900 text-ink-400 text-xs rounded border border-ink-800 flex items-center gap-2">
            <ImageIcon size={14} />
            <span>{filename || attachmentId || 'image'} — failed to load</span>
          </div>
        ) : (
          <div className="px-3 py-6 bg-ink-900 text-ink-500 text-xs rounded border border-ink-800">
            Decrypting image…
          </div>
        )}
        {editable && url && (
          <>
            {/* Hover/focus-revealed action strip: align L/C/R, alt-text edit,
                replace. Hidden by default to keep the prose clean; shown on
                hover via group-hover OR focus-within (keyboard).
                On touch (`@media (pointer: coarse)`) the strip is always
                visible — see styles.css — since `:hover` doesn't fire on
                tap. The hidden file input is rendered here (not portalled)
                so EditorPane's auto-Done click-out invariant stays correct —
                clicking the picker stays inside the editor section. */}
            <div
              data-action-strip
              className={
                'absolute top-1 right-1 flex items-center gap-0.5 rounded-md ' +
                'bg-ink-900/90 border border-ink-700 p-0.5 ' +
                'opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity'
              }
            >
              <button
                type="button"
                onClick={() => setAlign(align === 'left' ? null : 'left')}
                className={`p-1 rounded hover:bg-ink-800 ${align === 'left' ? 'bg-ink-800 text-white' : 'text-ink-300'}`}
                title="Align left"
              >
                <AlignLeft size={12} />
              </button>
              <button
                type="button"
                onClick={() => setAlign(align === 'center' ? null : 'center')}
                className={`p-1 rounded hover:bg-ink-800 ${align === 'center' ? 'bg-ink-800 text-white' : 'text-ink-300'}`}
                title="Align centre"
              >
                <AlignCenter size={12} />
              </button>
              <button
                type="button"
                onClick={() => setAlign(align === 'right' ? null : 'right')}
                className={`p-1 rounded hover:bg-ink-800 ${align === 'right' ? 'bg-ink-800 text-white' : 'text-ink-300'}`}
                title="Align right"
              >
                <AlignRight size={12} />
              </button>
              <span className="mx-0.5 h-4 w-px bg-ink-700" />
              <button
                type="button"
                onClick={startEditAlt}
                className="p-1 rounded hover:bg-ink-800 text-ink-300"
                title="Edit alt text"
              >
                <Pencil size={12} />
              </button>
              <button
                type="button"
                onClick={pickReplacement}
                className="p-1 rounded hover:bg-ink-800 text-ink-300"
                title="Replace image"
              >
                <RefreshCw size={12} />
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFilePicked}
            />
            <span
              data-resize-handle
              onPointerDown={startResize}
              contentEditable={false}
              title="Drag to resize"
              className="absolute right-0 bottom-0 w-3 h-3 bg-dragon-500 cursor-se-resize rounded-sm"
              style={{ transform: 'translate(50%, 50%)' }}
            />
          </>
        )}
        {editable && editingAlt && (
          <div className="absolute left-0 right-0 bottom-0 translate-y-full mt-1 flex items-center gap-1 bg-ink-900 border border-ink-700 rounded-md p-1 z-10">
            <input
              autoFocus
              value={altDraft}
              onChange={(e) => setAltDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitAlt();
                else if (e.key === 'Escape') cancelAlt();
              }}
              placeholder="Alt text (for accessibility)"
              className="flex-1 bg-transparent text-xs text-ink-100 placeholder-ink-500 focus:outline-none px-1"
            />
            <button type="button" onClick={commitAlt} className="p-1 rounded hover:bg-ink-800 text-ink-300" title="Save">
              <Check size={12} />
            </button>
            <button type="button" onClick={cancelAlt} className="p-1 rounded hover:bg-ink-800 text-ink-300" title="Cancel">
              <X size={12} />
            </button>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}
